import type { ContentKind, Direction, FileSummary, Mailbox, MailboxInfo } from "../api";
import { mailboxLabel } from "../api";
import { formatShortTimestamp, formatSize } from "../format";
import { FileNameLegend } from "./FileNameKey";

// The file list. Direction is named the way an operator thinks about it —
// received from the bank, or sent to it — never "inbound"/"outbound", which are
// the storage prefixes. The mailbox above it is named by its subsystem, which is
// how the bank's own file names end (`.BS2`). Each row leads with the delivery
// type, because that is what tells you whether a file is worth opening.

/** Files that are not deliveries are listed quietly — they carry no records. */
const QUIET: ReadonlySet<ContentKind> = new Set([
  "transmission_receipt",
  "human_readable_report",
  "sftp_admin",
]);

/** How the documentation names each kind, for the filter chip. */
const KIND_NAMES: Record<ContentKind, string> = {
  collections: "Collections (0601)",
  payment_advice: "Payment advice (0602)",
  mandate_information: "Mandate information (0603)",
  mandate_changes: "Mandate changes (0605)",
  validation_receipt: "Validation receipts",
  transmission_receipt: "Transmission receipts",
  human_readable_report: "Cover sheets and reports",
  sftp_admin: "SFTP administration",
  unrecognised: "Unrecognised",
};

const badgeOf = (file: FileSummary): string => {
  if (file.deliveryType) return file.deliveryType;
  if (file.contentKind === "validation_receipt") return "rcpt";
  if (file.contentKind === "unrecognised") return "?";
  return "—";
};

export function FileRail({
  mailboxes,
  mailbox,
  onMailbox,
  direction,
  onDirection,
  query,
  onQuery,
  files,
  kind,
  onClearKind,
  selected,
  onSelect,
  status,
}: {
  mailboxes: MailboxInfo[];
  mailbox: Mailbox;
  onMailbox: (mailbox: Mailbox) => void;
  direction: Direction;
  onDirection: (direction: Direction) => void;
  query: string;
  onQuery: (query: string) => void;
  files: FileSummary[];
  /** Set when the documentation page sent the reader here to see one kind. */
  kind?: ContentKind;
  onClearKind: () => void;
  selected?: string;
  onSelect: (name: string) => void;
  status: string;
}): JSX.Element {
  return (
    <aside className="rail">
      <div className="segmented" role="group" aria-label="Which login's files to list">
        {mailboxes.map((info) => (
          <button key={info.id} type="button" aria-pressed={info.id === mailbox} onClick={() => onMailbox(info.id)}>
            {mailboxLabel(info)}
          </button>
        ))}
      </div>
      <div className="segmented" role="group" aria-label="Which files to list">
        <button type="button" aria-pressed={direction === "inbound"} onClick={() => onDirection("inbound")}>
          Received
        </button>
        <button type="button" aria-pressed={direction === "outbound"} onClick={() => onDirection("outbound")}>
          Sent
        </button>
      </div>

      <div className="search">
        <input
          type="search"
          value={query}
          placeholder="Filter by file name"
          aria-label="Filter by file name"
          onChange={(event) => onQuery(event.target.value)}
        />
        {kind !== undefined ? (
          <button type="button" className="kind-filter" onClick={onClearKind}>
            {KIND_NAMES[kind]}
            <span aria-hidden="true">×</span>
            <span className="visually-hidden">— show every kind again</span>
          </button>
        ) : null}
      </div>

      <div className="file-list">
        {files.map((file) => (
          <button
            type="button"
            key={file.name}
            className="file-row"
            aria-current={file.name === selected}
            onClick={() => onSelect(file.name)}
            title={file.naming?.summary ?? file.label ?? undefined}
          >
            <span className="type" data-kind={file.contentKind} data-quiet={QUIET.has(file.contentKind)}>
              {badgeOf(file)}
            </span>
            <span className="file-text">
              <span className="name">{file.name}</span>
              <span className="meta">
                {formatShortTimestamp(file.lastModified)} · {formatSize(file.size)}
                {file.deliveryType ? "" : ` · ${file.label ?? file.contentKind}`}
              </span>
            </span>
          </button>
        ))}
      </div>

      <div className="rail-foot">
        <span>{status}</span>
        <FileNameLegend />
      </div>
    </aside>
  );
}
